import { NessieThemeColors } from "@classdojo/web/nessie/components/theme";

const SIZES = {
  xs: "12px",
  s: "18px",
  m: "24px",
  l: "36px",
  xl: "48px",
  xxl: "56px",
};

type IdeasIconProps = React.SVGProps<SVGSVGElement> & {
  onClick?: VoidFunction;
  cursor?: string;
  display?: string;
  color?: NessieThemeColors;
  size?: keyof typeof SIZES;
  "data-name"?: string;
};

const IdeasAddIcon = ({
  size = "m",
  color = "dt_content_accent",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M14 10h7a2 2 0 110 4h-7v7a2 2 0 11-4 0v-7H3a2 2 0 110-4h7V3a2 2 0 114 0v7z"
        />
        <mask id="AddIcon_svg__a" maskUnits="userSpaceOnUse" x={1} y={1} width={22} height={22}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 10h7a2 2 0 110 4h-7v7a2 2 0 11-4 0v-7H3a2 2 0 110-4h7V3a2 2 0 114 0v7z"
          />
        </mask>
      </svg>
    </span>
  );
};

const IdeasCloseIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick = () => {},
  cursor = undefined,
  display = "inline-block",
  ["data-name"]: dataName = undefined,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12 9.172l4.243-4.243a2 2 0 112.828 2.828L14.828 12l4.243 4.243a2 2 0 01-2.828 2.828L12 14.828l-4.243 4.243a2 2 0 01-2.828-2.828L9.172 12 4.929 7.757A2 2 0 117.757 4.93L12 9.172z"
        />
      </svg>
    </span>
  );
};

const IdeasCheckmarkIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M8.421 19.122a1.996 1.996 0 001.565-.58L20.434 8.093a2 2 0 10-2.828-2.828L8.421 14.45 5.443 11.47A2 2 0 102.614 14.3l4.243 4.242a1.996 1.996 0 001.564.58z" />
      </svg>
    </span>
  );
};

const IdeasChevronLeftIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M6.2 11.984a1.994 1.994 0 00.586 1.43l6.93 6.93a2 2 0 002.828-2.829l-5.53-5.53 5.53-5.531a2 2 0 00-2.828-2.829l-6.93 6.93c-.394.394-.59.912-.586 1.43z" />
      </svg>
    </span>
  );
};

const IdeasLockIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.347 9.829h-.896V6.401C16.451 3.986 14.465 2 12.05 2S7.649 3.986 7.649 6.401V9.83h-.896c-.974 0-1.753.779-1.753 1.753l.779 8.14c0 .974.779 1.753 1.753 1.753h9.036c.974 0 1.753-.779 1.753-1.753l.779-8.14c0-.935-.779-1.753-1.753-1.753zm-4.362 8.803h-1.83l-.351-2.922c0-.7.584-1.285 1.285-1.285s1.285.584 1.285 1.285l-.39 2.922zm1.597-8.803H9.518V6.401A2.527 2.527 0 0112.05 3.87 2.527 2.527 0 0114.582 6.4V9.83z"
        />
        <mask id="LockIcon_svg__a" maskUnits="userSpaceOnUse" x={5} y={2} width={15} height={20}>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.347 9.829h-.896V6.401C16.451 3.986 14.465 2 12.05 2S7.649 3.986 7.649 6.401V9.83h-.896c-.974 0-1.753.779-1.753 1.753l.779 8.14c0 .974.779 1.753 1.753 1.753h9.036c.974 0 1.753-.779 1.753-1.753l.779-8.14c0-.935-.779-1.753-1.753-1.753zm-4.362 8.803h-1.83l-.351-2.922c0-.7.584-1.285 1.285-1.285s1.285.584 1.285 1.285l-.39 2.922zm1.597-8.803H9.518V6.401A2.527 2.527 0 0112.05 3.87 2.527 2.527 0 0114.582 6.4V9.83z"
          />
        </mask>
      </svg>
    </span>
  );
};

const IdeasShuffleIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.7 10.491V9h-2.32c-.762 0-1.237.306-1.65.852-.463.615-.794 1.467-1.195 2.507l-.01.024c-.37.963-.809 2.102-1.5 2.984C9.274 16.323 8.212 17 6.686 17H3a1 1 0 010-2h3.687c.825 0 1.336-.322 1.764-.867.479-.61.82-1.46 1.218-2.492l.044-.116c.36-.935.78-2.027 1.42-2.877C11.854 7.694 12.882 7 14.38 7h2.32V5.509a.5.5 0 01.803-.398l3.274 2.491a.5.5 0 010 .796l-3.274 2.491a.5.5 0 01-.803-.398zm0 4.51v-1.492a.5.5 0 01.803-.398l3.274 2.491a.5.5 0 010 .796l-3.274 2.491a.5.5 0 01-.803-.398v-1.49h-2.32c-1.237 0-2.153-.474-2.84-1.175.422-.635.747-1.324 1.006-1.945.06.094.12.182.184.267.413.546.888.852 1.65.852h2.32zM6.687 7c1.276 0 2.226.472 2.942 1.185-.41.638-.729 1.332-.983 1.954a3.973 3.973 0 00-.195-.273C8.023 9.323 7.512 9 6.687 9H3a1 1 0 110-2h3.687z"
        />
      </svg>
    </span>
  );
};

const IdeasPlayIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.068 1.938C6.736 1.097 5 2.054 5 3.628v16.743c0 1.576 1.736 2.532 3.068 1.691l13.255-8.371a2 2 0 000-3.382L8.068 1.938z"
        />
      </svg>
    </span>
  );
};

const IdeasPauseIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path fillRule="evenodd" clipRule="evenodd" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        <mask id="PauseIcon_svg__a" maskUnits="userSpaceOnUse" x={6} y={5} width={12} height={14}>
          <path fillRule="evenodd" clipRule="evenodd" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
        </mask>
      </svg>
    </span>
  );
};

const IdeasPencilIcon = ({
  size = "m",
  color = "dt_content_primary",
  onClick,
  cursor,
  display = "inline-block",
  ["data-name"]: dataName,
  ...props
}: IdeasIconProps) => {
  const sizeInPixels = SIZES[size];
  return (
    <span
      data-name={dataName}
      onClick={onClick}
      sx={{
        fontSize: sizeInPixels,
        display,
        lineHeight: 0,
        color,
        cursor,
        position: "relative",
      }}
    >
      <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M18.137 3.162l2.71 2.709c.107.108.18.252.144.397a.543.543 0 01-.145.397l-.036.036-3.055 3.054-3.488-3.488 1.81-1.805.037-.036.867-.866.397-.398a.524.524 0 01.759 0zm-5.373 4.602L4.987 15.51c-.036.037-.108.109-.108.181l-1.843 4.586c-.072.216-.036.433.108.578a.544.544 0 00.398.144c.072 0 .144 0 .217-.036l2.89-1.156 1.661-.686a.79.79 0 00.181-.108l7.763-7.76-3.49-3.49z"
        />
      </svg>
    </span>
  );
};

export {
  IdeasAddIcon,
  IdeasCloseIcon,
  IdeasCheckmarkIcon,
  IdeasChevronLeftIcon,
  IdeasLockIcon,
  IdeasShuffleIcon,
  IdeasPlayIcon,
  IdeasPauseIcon,
  IdeasPencilIcon,
};
